<template>
  <div
    class="preview-wrapper"
    ref="wrapper"
    :class="wrapperClasses"
    :style="prevWrapInlineStyle"
  >
    <transition-group
      ref="previewList"
      tag="div"
      class="preview"
      enter-active-class="zoomIn"
      leave-active-class="zoomOut"
    >
      <div
        v-for="(file, i) in imagePreview"
        :key="file.name"
        class="preview-image"
      >
        <div
          v-if="showButtonClose" class="preview-remove"
          @click="onRemoveImageClick(i)"
        >&times;</div>
        <PreviewFileGenerator
          :src="file.src"
          :filename="file.file.name"
          :size="bytesToSize(file.file.size)"
          :extension="getExtensions(file.file)"
          type="preview"
          @click="file.file.type.startsWith('image/') && editImage(i)"
        />
        <div
          v-if="showMetaInformation"
          class="preview-info">
          <span>{{file.file.name}}</span>
            {{ bytesToSize(file.file.size) }}
        </div>
      </div>
    </transition-group>
  </div>
</template>
<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import PreviewFileGenerator from '@/components/elements/PreviewFileGenerator';
import { readFileAsDataURL, editImageWithPainterro, dataURLtoFile } from '@/store/utils';
import { useSortable } from '@/composables/useSortable';

const props = defineProps({
  wrapperClasses: {
    type: [String, Array, Object],
    default: '',
  },
  initPreviewImage: {
    type: [FileList, Array],
    default: null,
  },
  showMetaInformation: {
    type: Boolean,
    default: false,
  },
  showButtonClose: {
    type: Boolean,
    default: true,
  },
  limitPreview: {
    type: Number,
    default: 10,
  },
});

const emit = defineEmits(['mount', 'startUpload', 'onceStartUpload', 'upload', 'remove', 'empty', 'unmounted', 'error', 'reorder']);

const wrapper = ref(null);
const previewList = ref(null);
const imagePreview = ref([]);
const isOnceUploaded = ref(false);

const prevWrapInlineStyle = computed(() => ({
  'pointer-events': imagePreview.value.length ? 'auto' : 'none',
}));

function getExtensions(file) {
  const extension = file.name.split('.');
  return extension[extension.length - 1];
}

function getHeight() {
  return parseInt(window.getComputedStyle(wrapper.value).height, 10);
}

function createObjSimpleFilesList(fileListArray) {
  const FileList = fileListArray.reduce((accum, currentVal, index) => {
    // eslint-disable-next-line
    accum[index] = currentVal.file;
    // eslint-disable-next-line
    accum[index].kind = 'file';
    return accum;
  }, {});
  FileList.length = fileListArray.length;
  return FileList;
}

function imageObj(file, event) {
  return {
    file,
    src: event.target.result,
    name: `${file.name}${Date.now()}`,
  };
}

function bytesToSize(bytes) {
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];

  if (!bytes) return '0 Byte';

  const i = parseInt(Math.floor(Math.log(bytes) / Math.log(1024)), 10);

  return `${Math.round(bytes / (1024 ** i))}  ${sizes[i]}`;
}

function createPreviewImage(file) {
  readFileAsDataURL(file).then((dataUrl) => {
    if (imagePreview.value.length >= props.limitPreview) {
      emit('error', { limit: props.limitPreview });
      return;
    }
    const lastImage = imagePreview.value.push(imageObj(file, { target: { result: dataUrl } })) - 1;
    console.log('imagePreview', imagePreview.value.map(preview => ({
      name: preview.name,
      type: preview.file.type,
      size: preview.file.size,
      src: `<base64 hidden, ${preview.src.length} chars>`,
    })));

    const upload = {
      image: imagePreview.value[lastImage],
      imagePreview: imagePreview.value,
    };

    emit('upload', upload);
  })
  .catch((error) => {
    console.error('Error reading file:', error);
  });
}

function createPreviewImages(eventFiles) {
  const files = Array.from(eventFiles);

  emit('startUpload', eventFiles);
  files.forEach((file) => {
    createPreviewImage(file);
  });
}

function onRemoveImageClick(id) {
  const removeElm = imagePreview.value.splice(id, 1);

  const removeEvent = {
    image: removeElm,
    imagePreview: imagePreview.value,
    removeId: id,
  };

  emit('inputChange', false);
  emit('remove', removeEvent);

  if (!imagePreview.value.length) emit('empty');
}

function editImage(index) {
  const item = imagePreview.value[index];
  editImageWithPainterro({
    imageUrl: item.src,
    onSave: async (imageData) => {
      const dataUrl = imageData.asDataURL();
      const extension = dataUrl.split(';')[0].split('/')[1] || 'png';
      const baseName = item.file.name.replace(/\.[^./]+$/, '') || 'image';
      item.file = await dataURLtoFile(dataUrl, `${baseName}.${extension}`);
      item.src = dataUrl;
    },
  });
}

function onPreviewDragEnd(evt) {
  if (evt.oldIndex === evt.newIndex) return;
  const [moved] = imagePreview.value.splice(evt.oldIndex, 1);
  imagePreview.value.splice(evt.newIndex, 0, moved);
  emit('reorder', [...imagePreview.value]);
}

useSortable(previewList, () => (props.showButtonClose ? {
  animation: 150,
  delay: 250,
  delayOnTouchOnly: true,
  filter: '.preview-remove',
  preventOnFilter: false,
  ghostClass: 'preview-image-ghost',
  chosenClass: 'preview-image-chosen',
  onEnd: onPreviewDragEnd,
} : null), [() => imagePreview.value.length, () => props.showButtonClose]);

watch(() => props.initPreviewImage, (value) => {
  if (!value?.length) return;
  const existing = new Set(imagePreview.value.map(p => p.file));
  const newFiles = Array.from(value).filter(f => !existing.has(f));
  if (newFiles.length) {
    createPreviewImages(newFiles);
  }
});

onMounted(() => {
  emit('height', getHeight());
  emit('mount');

  if (props.initPreviewImage.length) {
    createPreviewImages(props.initPreviewImage);
  }
});

onBeforeUnmount(() => {
  emit('unmounted');
});

defineExpose({
  createObjSimpleFilesList,
});
</script>
<style lang="scss" scoped>
  .zoomOut {
    animation: zoomOut 0.22s;
  }
  .zoomIn {
    animation: zoomIn 0.22s;
  }
  @keyframes zoomIn {
    from {transform: scale(0)}
    to {transform: scale(1)}
  }
  @keyframes zoomOut {
    from{transform: scale(1);}
    to{transform: scale(0);}
  }
  .preview-wrapper {
    width: 95%;
    overflow-x: auto;
    overflow-y: hidden;
    display: flex;
    align-items: center;
    padding-left: 1px;
  }
  .preview {
    display: flex;
    flex-wrap: nowrap;
    span {
      display: flex;
      flex-wrap: nowrap;
      width: 100%;
    }
  }
  .preview-badge {
    position: relative;
    margin-right: .5rem;
    background-color: gray;
    width: 62px;
    height: 62px;
  }
  .preview-image {
    display: flex;
    align-items: center;
    position: relative;
    margin-right: .5rem;
    max-height: 75px;
    cursor: grab;
    &.preview-image-ghost {
      opacity: 0.4;
    }
    &.preview-image-chosen {
      cursor: grabbing;
    }
    img {
      width: 62px;
      height: 62px;
      object-fit: cover;
      border-radius: 6px;
      &:last-child {
        margin-right: 0px;
      }
    }
    &:hover {
      .preview-remove {
        opacity: 1;
      }
      .preview-info {
        bottom: 0px;
      }
    }
  }
  .preview-remove {
    opacity: 0;
    width: 20px;
    height: 20px;
    position: absolute;
    right: -10px;
    top: -7px;
    font-weight: bold;
    background: rgba(0, 0, 0, 0.4);
    color: white;
    border-radius: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: opacity .22s;
    cursor: pointer;
    transition: all 0.22s;
    z-index: 10;
    &:hover {
      background: rgba(0, 0, 0, 0.8);
    }
  }
  .preview-info {
    position: absolute;
    height: 25px;
    right: 0;
    bottom: -30px;
    left: 0;
    font-size: 0.7rem;
    background: rgba(255, 255,255,.5);
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 5px;
    transition: bottom 0.15s;
  }
</style>
